import { useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
} from '@travellegacy/nexops-design-system';
import { BarChart3 } from 'lucide-react';
import { useDemandsFacets, useDistribution } from '../hooks/useDemands';
import { formatDurationRich } from '../utils/format-duration';

interface Props {
  periodFilters: { from?: string; to?: string };
}

/**
 * Card com histograma + percentis (p50/p90/p99) da duração dos ciclos fechados.
 * Permite filtrar por tipo (dropdown). Sem filtro = visão geral.
 */
export function DistributionCard({ periodFilters }: Props) {
  const [type, setType] = useState<string>('');
  const facetsQuery = useDemandsFacets();

  const filters = useMemo(
    () => ({
      ...periodFilters,
      type: type || undefined,
    }),
    [periodFilters, type]
  );

  const distributionQuery = useDistribution(filters);
  const data = distributionQuery.data?.data;

  const typeOptions = useMemo(
    () => [
      { value: '', label: 'Todos os tipos' },
      ...(facetsQuery.data?.data.types ?? []).map((t) => ({
        value: t,
        label: t,
      })),
    ],
    [facetsQuery.data]
  );

  const maxCount = useMemo(() => {
    if (!data) return 0;
    return data.buckets.reduce((max, b) => Math.max(max, b.count), 0);
  }, [data]);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Distribuição de duração dos ciclos
          </CardTitle>
          <div className="w-48">
            <Select
              value={type}
              onChange={(e) => setType(e.target.value)}
              options={typeOptions}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {distributionQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Carregando…</p>
        ) : !data || data.count === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nenhum ciclo fechado no período com esses filtros.
          </p>
        ) : (
          <>
            {/* Percentis + estatísticas */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 mb-6">
              <Stat label="ciclos" value={data.count.toString()} />
              <Stat label="mediana (p50)" value={formatDurationRich(data.p50Ms)} />
              <Stat label="p90" value={formatDurationRich(data.p90Ms)} />
              <Stat label="p99" value={formatDurationRich(data.p99Ms)} />
              <Stat label="média" value={formatDurationRich(data.avgMs)} />
              <Stat label="máximo" value={formatDurationRich(data.maxMs)} />
            </div>

            {/* Histograma horizontal */}
            <div className="space-y-2">
              {data.buckets.map((bucket) => {
                const pct =
                  maxCount > 0 ? (bucket.count / maxCount) * 100 : 0;
                const share =
                  data.count > 0
                    ? Math.round((bucket.count / data.count) * 100)
                    : 0;
                return (
                  <div key={bucket.label} className="flex items-center gap-3">
                    <div className="w-20 text-xs text-muted-foreground tabular-nums">
                      {bucket.label}
                    </div>
                    <div className="flex-1 h-6 rounded-md bg-muted relative overflow-hidden">
                      <div
                        className="h-full bg-primary/70 transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="w-24 text-right text-xs tabular-nums text-muted-foreground">
                      {bucket.count} ({share}%)
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
